import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import { NumericKeyboardInput } from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import { adjustmentBalance } from '@/models/balance';
import { User } from '@/models/user';
import { PlusOutlined } from '@ant-design/icons';
import { Alert, Form, Select, Space } from 'antd';
import { FormInstance } from 'antd/es/form';
import { useForm } from 'antd/es/form/Form';
import { ModalProps } from 'antd/es/modal';
import { BigNumber } from 'bignumber.js';
import React, { useState, useRef } from 'react';

enum ActionType {
  ADD = 1,
  MINUS,
}

const typeMap = {
  [ActionType.ADD]: {
    text: '充入',
    factor: 1,
    api: 'plus',
  },
  [ActionType.MINUS]: {
    text: '扣减',
    factor: -1,
    api: 'minus',
  },
};

type BalanceAdjustmentFormProps = {
  userId: User['id'];
  actionType: ActionType;
  form: FormInstance;
  onSuccess: (v: BigNumber) => void;
};

const BalanceAdjustmentForm: React.FC<BalanceAdjustmentFormProps> = ({
  userId,
  actionType,
  form,
  onSuccess,
}: BalanceAdjustmentFormProps) => {
  const isSubmitting = useRef(false);

  const change = async (
    amount: BigNumber,
    type: number,
    comment: string,
    userId: BalanceAdjustmentFormProps['userId']
  ) => {
    if (isSubmitting.current) {
      message.warning('请不要重复提交');
      return;
    }
    const hideLoading = message.loading('处理中...');
    isSubmitting.current = true;
    try {
      const updatedAmount = await adjustmentBalance({ userId, amount, comment, type });
      message.success('操作成功，余额已变更');
      onSuccess(new BigNumber(updatedAmount));
    } catch (e) {
      message.error(`操作失败: ${e.msg}`);
      isSubmitting.current = false;
    } finally {
      hideLoading();
    }
  };

  return (
    <Form
      form={form}
      onFinish={({ amount, type, comment }) => {
        if (amount) {
          change(new BigNumber(amount), type, comment, userId);
        } else {
          message.error('请输入有效的积分值');
        }
      }}
      initialValues={{
        type: 102,
        amount: '',
      }}
      layout={'vertical'}>
      <Alert
        className="mb-l"
        message={
          <>
            您当前正在进行<span className="emphasize red">余额手动调整</span>，请谨慎操作
          </>
        }
        type="warning"
        showIcon
      />
      <Form.Item name="amount" colon={false} label={`${typeMap[actionType].text}金额`} required>
        <NumericKeyboardInput
          inputProps={{
            placeholder: '金额 (元)',
            min: 0.01,
            precision: 2,
          }}
          keyboardProps={{
            parseValueToNumber: true,
            precision: 2,
            showAllClean: true,
            placement: 'top',
            trigger: 'click',
          }}
        />
      </Form.Item>
      <Form.Item name="type" colon={false} label={'变动类型'}>
        <Select>
          <Select.Option value={102}>抽奖活动奖励</Select.Option>
        </Select>
      </Form.Item>
    </Form>
  );
};

type FormModalProps = ModalProps & Pick<BalanceAdjustmentFormProps, 'userId' | 'onSuccess'>;

export const BalanceOperator = ({ userId, onSuccess: onSuccessProp, ...modalProps }: FormModalProps) => {
  const [actionType, setActionType] = useState<ActionType>();
  const [form] = useForm();

  const onSuccess = (updated: BigNumber) => {
    setActionType(null);
    onSuccessProp(updated);
    form.resetFields();
  };

  return (
    <>
      <Modal
        {...modalProps}
        title="余额变动操作"
        destroyOnHidden
        open={Boolean(actionType)}
        onOk={async () => {
          form.submit();
        }}
        onCancel={() => {
          setActionType(null);
          form.resetFields();
        }}>
        <BalanceAdjustmentForm form={form} actionType={actionType} userId={userId} onSuccess={onSuccess} />
      </Modal>
      <Space>
        <PosButton
          color="primary"
          variant="outlined"
          icon={<PlusOutlined />}
          onClick={() => {
            setActionType(ActionType.ADD);
          }}>
          追加余额
        </PosButton>
      </Space>
    </>
  );
};
